import React, { useEffect, useState } from 'react';
import { Course, Tag, TagType } from '../../apis/api-client';
import { CourseAutocomplete, SettingsCard, TagAutocomplete } from '../common';
import { courseTypeMeta } from '../courses/contentTypeMeta';
import { CloseIcon, SearchIcon } from '../icons';
import { Avatar, Box, Chip, IconButton, Tooltip, Typography } from '../ui';
import { getFileUrl } from '../../hooks/function';

/**
 * Resolves SAS-signed thumbnail URLs for a list of courses and caches
 * them by course id so re-renders don't refetch.
 */
function useCourseThumbnails(courses: Course[]): Record<number, string> {
  const [urls, setUrls] = useState<Record<number, string>>({});

  useEffect(() => {
    let cancelled = false;
    const toLoad = courses.filter((c) => c.id && c.thumbnail && !urls[c.id]);
    if (toLoad.length === 0) return;

    Promise.all(
      toLoad.map(async (c) => {
        const resolved = await getFileUrl(c.thumbnail!);
        return [c.id!, resolved || ''] as const;
      }),
    ).then((pairs) => {
      if (cancelled) return;
      setUrls((prev) => {
        const next = { ...prev };
        for (const [id, url] of pairs) if (url) next[id] = url;
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses.map((c) => `${c.id}:${c.thumbnail}`).join('|')]);

  return urls;
}

interface WorkspaceAccessTabProps {
  // Controlled: the grant collections live in the page so the shared save
  // bar (handleAccessSave) can send the full UpdateWorkspaceAccessRequest.
  contentTags: Tag[];
  setContentTags: React.Dispatch<React.SetStateAction<Tag[]>>;
  provisionedContent: Course[];
  setProvisionedContent: React.Dispatch<React.SetStateAction<Course[]>>;
}

/**
 * WorkspaceAccessTab
 *
 * The Access tab body for the workspace editor: tag-based content grants
 * plus an explicit provisioned-content list. Extracted from WorkspaceEdit
 * as a CONTROLLED component — the grant state stays in the page because
 * its full-replace save sends tags + provisioned ids together. Thumbnail
 * resolution for the provisioned list is owned here.
 */
export default function WorkspaceAccessTab({
  contentTags,
  setContentTags,
  provisionedContent,
  setProvisionedContent,
}: WorkspaceAccessTabProps) {
  const provisionedThumbs = useCourseThumbnails(provisionedContent);

  return (
    <Box sx={{ maxWidth: 920, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SettingsCard
        title="Content access by tag"
        subtitle="Any course or learning path tagged with one of these is automatically available to this workspace."
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2.5,
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { md: 'flex-start' },
          }}
        >
          {/* Left: compact search input — single-select acting as
              "search to add". Width capped so it doesn't dominate
              the row and reads as a search affordance, not a
              multi-value form field. */}
          <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
            <TagAutocomplete
              value={null}
              onChange={(v) => {
                const picked = Array.isArray(v) ? v[0] : v;
                if (!picked) return;
                if (contentTags.some((t) => t.id === picked.id)) return;
                setContentTags((cur) => [...cur, picked]);
              }}
              tagType={TagType.CourseTag}
              placeholder="Search tags…"
              label="Add a tag"
            />
          </Box>

          {/* Right: granted-tags list. Renders as soft outlined
              pill chips with a remove ×. Empty state is a quiet
              dashed placeholder so the column never collapses. */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}
            >
              {contentTags.length > 0
                ? `${contentTags.length} tag${contentTags.length === 1 ? '' : 's'} granted`
                : 'No tags granted yet'}
            </Typography>
            {contentTags.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {contentTags.map((t) => (
                  <Chip
                    key={t.id}
                    label={t.name}
                    onDelete={() =>
                      setContentTags((cur) => cur.filter((x) => x.id !== t.id))
                    }
                    deleteIcon={<CloseIcon />}
                    sx={{
                      height: 30,
                      px: 0.5,
                      fontWeight: 600,
                      bgcolor: 'rgba(var(--brand-rgb), 0.14)',
                      color: 'primary.main',
                      border: '1px solid',
                      borderColor: 'rgba(var(--brand-rgb), 0.45)',
                      '& .MuiChip-deleteIcon': {
                        color: 'primary.main',
                        opacity: 0.7,
                        '&:hover': { opacity: 1, color: 'primary.dark' },
                      },
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  border: '1px dashed',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                  color: 'text.secondary',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2">
                  Search for a tag to grant it to this workspace.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </SettingsCard>

      <SettingsCard
        title="Provisioned content"
        subtitle="Specific courses, learning paths, and events granted to this workspace, in addition to anything matched by tag rules above."
      >
        <Box>
          {/* Single-select picker — each pick appends to the list
              below and clears the autocomplete so it behaves like
              an "add to list" interface instead of a chip cluster
              inside the input. */}
          <CourseAutocomplete
            value={null}
            onChange={(v) => {
              const picked = Array.isArray(v) ? v[0] : v;
              if (!picked) return;
              // Skip dupes — autocomplete options aren't filtered
              // against the current selection upstream.
              if (provisionedContent.some((c) => c.id === picked.id)) return;
              setProvisionedContent((cur) => [...cur, picked]);
            }}
            placeholder="Search courses, paths, and events…"
            label="Search content"
          />

          {/* Visual list of provisioned items — replaces the
              inline chip pile inside the autocomplete. Thumbnail
              + type chip + title + remove. */}
          {provisionedContent.length > 0 ? (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
              >
                {provisionedContent.length} item{provisionedContent.length === 1 ? '' : 's'} provisioned
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                {provisionedContent.map((c, idx) => {
                  const meta = courseTypeMeta(c.courseType);
                  const thumb = c.id ? provisionedThumbs[c.id] : undefined;
                  return (
                    <Box
                      key={c.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 1.5,
                        py: 1,
                        bgcolor: 'background.paper',
                        borderBottom: idx === provisionedContent.length - 1 ? 0 : '1px solid',
                        borderColor: 'divider',
                        '&:hover': { bgcolor: 'action.hover' },
                        transition: 'background-color 120ms ease',
                      }}
                    >
                      {/* Thumbnail or initials avatar tinted with
                          the type's accent color when no image. */}
                      <Avatar
                        src={thumb}
                        alt={c.title || ''}
                        variant="rounded"
                        sx={{
                          width: 48,
                          height: 36,
                          bgcolor: thumb ? 'transparent' : `${meta.color}18`,
                          color: meta.color,
                          border: '1px solid',
                          borderColor: 'divider',
                          flexShrink: 0,
                        }}
                      >
                        {meta.icon}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {c.title || `Course #${c.id}`}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                          <Chip
                            label={meta.label}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: `${meta.color}15`,
                              color: meta.color,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                          {c.isDraft && (
                            <Chip
                              label="Draft"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                bgcolor: 'grey.100',
                                color: 'text.secondary',
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setProvisionedContent((cur) => cur.filter((x) => x.id !== c.id))
                          }
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                mt: 2,
                p: 3,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: 'grey.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                color: 'text.secondary',
              }}
            >
              <SearchIcon fontSize="small" />
              <Typography variant="body2">
                No content provisioned directly. Search above to grant
                a specific course, learning path, or event.
              </Typography>
            </Box>
          )}
        </Box>
      </SettingsCard>
    </Box>
  );
}
